/**
 * Centralized logging utility for Doc2Plot frontend
 * Provides consistent logging with environment-based filtering
 */

const DEBUG = ((import.meta as any).env?.VITE_DEBUG === 'true') || ((import.meta as any).env?.MODE !== 'production')

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

class Logger {
  private logLevel: LogLevel = DEBUG ? LogLevel.DEBUG : LogLevel.INFO

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel
  }

  private formatMessage(level: string, message: string): string {
    const timestamp = new Date().toISOString()
    return `[${timestamp}] [${level}] ${message}`
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', message), ...args)
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('INFO', message), ...args)
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message), ...args)
    }
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message), ...args)
    }
  }

  // Specialized logging methods for common patterns
  appInit(message: string, ...args: any[]): void {
    this.info(`🚀 ${message}`, ...args)
  }

  appSuccess(message: string, ...args: any[]): void {
    this.info(`✅ ${message}`, ...args)
  }

  appError(message: string, ...args: any[]): void {
    this.error(`❌ ${message}`, ...args)
  }

  appWarning(message: string, ...args: any[]): void {
    this.warn(`⚠️ ${message}`, ...args)
  }

  auth(message: string, ...args: any[]): void {
    this.debug(`🔐 ${message}`, ...args)
  }

  pdf(message: string, ...args: any[]): void {
    this.debug(`📄 ${message}`, ...args)
  }

  ocr(message: string, ...args: any[]): void {
    this.debug(`🔍 ${message}`, ...args)
  }

  ai(message: string, ...args: any[]): void {
    this.debug(`🤖 ${message}`, ...args)
  }

  plot(message: string, ...args: any[]): void {
    this.debug(`📐 ${message}`, ...args)
  }

  api(message: string, ...args: any[]): void {
    this.debug(`🌐 ${message}`, ...args)
  }
}

// Export singleton instance
export const logger = new Logger()

// Export for backward compatibility
export default logger
